import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  Fab,
  Alert,
  Snackbar,
  Divider,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Phone,
  Email,
  LocationOn,
  Schedule,
  WhatsApp,
  Facebook,
  Instagram,
  Twitter,
  LinkedIn,
  Send,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import TopBar from '../website/TopBar';
import NavigationBar from '../website/NavigationBar';
import Footer from '../website/Footer';

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const contactInfo = [
    {
      icon: <Phone sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Phone',
      details: '+234 806 551 4429',
      subtitle: '24/7 Customer Support',
      action: 'tel:+2348065514429',
    },
    {
      icon: <Email sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Email',
      details: 'info@grandluxe.com',
      subtitle: 'We respond within 2 hours',
      action: 'mailto:info@grandluxe.com',
    },
    {
      icon: <LocationOn sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Address',
      details: '1 Bria Street, Wuse 2',
      subtitle: 'Abuja, Nigeria',
      action: 'https://maps.google.com/?q=1+Bria+Street+Wuse+2+Abuja',
    },
    {
      icon: <Schedule sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Working Hours',
      details: '24/7 Reception',
      subtitle: 'Restaurants: 6AM - 11PM',
    },
  ];

  const socialLinks = [
    { icon: <Facebook />, name: 'Facebook', url: 'https://facebook.com/grandluxe' },
    { icon: <Instagram />, name: 'Instagram', url: 'https://instagram.com/grandluxe' },
    { icon: <Twitter />, name: 'Twitter', url: 'https://twitter.com/grandluxe' },
    { icon: <LinkedIn />, name: 'LinkedIn', url: 'https://linkedin.com/company/grandluxe' },
  ];

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    // Simulate form submission
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setSnackbar({
        open: true,
        message: 'Your message has been sent successfully! We will get back to you soon.',
        severity: 'success'
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to send message. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Top Bar */}
      <TopBar />

      {/* Navigation */}
      <NavigationBar />

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.9) 0%, rgba(66, 165, 245, 0.8) 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              textAlign: 'center',
              position: 'relative',
              zIndex: 2,
            }}
          >
            <Chip
              label="Get In Touch"
              color="secondary"
              sx={{ mb: 2, color: 'white', fontWeight: 'bold' }}
            />
            <Typography
              variant="h2"
              fontWeight="bold"
              gutterBottom
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              }}
            >
              Contact Us
            </Typography>
            <Typography
              variant="h6"
              sx={{
                maxWidth: 600,
                mx: 'auto',
                opacity: 0.9,
                fontSize: { xs: '1.1rem', md: '1.25rem' },
              }}
            >
              We're here to help you plan your perfect stay. Reach out to us with any questions or special requests.
            </Typography>
          </Box>
        </Container>

        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            opacity: 0.5,
          }}
        />
      </Box>

      {/* Contact Information */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          {contactInfo.map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                    borderColor: 'primary.main',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ mb: 2 }}>{item.icon}</Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                    {item.title}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" gutterBottom>
                    {item.details}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {item.subtitle}
                  </Typography>
                  {item.action && (
                    <Button
                      variant="outlined"
                      size="small"
                      href={item.action}
                      target={item.action.startsWith('http') ? '_blank' : '_self'}
                      rel="noopener noreferrer"
                    >
                      {item.title === 'Phone' ? 'Call Now' : item.title === 'Email' ? 'Send Email' : 'Get Directions'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Contact Form Section */}
      <Container maxWidth="lg" sx={{ pb: 8 }}>
        <Grid container spacing={6}>
          {/* Contact Form */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                background: 'linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%)',
                height: 'fit-content',
              }}
            >
              <Typography variant="h4" fontWeight="bold" gutterBottom color="primary">
                Send us a Message
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Fill out the form below and our team will get back to you as soon as possible.
              </Typography>

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      value={formData.name}
                      onChange={handleInputChange('name')}
                      required
                      variant="outlined"
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange('email')}
                      required
                      variant="outlined"
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={formData.phone}
                      onChange={handleInputChange('phone')}
                      variant="outlined"
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Subject"
                      value={formData.subject}
                      onChange={handleInputChange('subject')}
                      required
                      variant="outlined"
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Message"
                      value={formData.message}
                      onChange={handleInputChange('message')}
                      required
                      multiline
                      rows={6}
                      variant="outlined"
                      sx={{ mb: 3 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading}
                      startIcon={loading ? null : <Send />}
                      sx={{
                        borderRadius: 3,
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        minWidth: 200,
                      }}
                    >
                      {loading ? (
                        <>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              border: '2px solid transparent',
                              borderTop: '2px solid white',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite',
                              mr: 1,
                            }}
                          />
                          Sending...
                        </>
                      ) : (
                        'Send Message'
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          
        </Grid>
      </Container>

      {/* Full Width Map Section */}
     {/* Full Width Map Section */}
<Box sx={{ width: '100%', pb: 8 }}>
  <Container maxWidth="xl" sx={{ px: { xs: 0, md: 2 } }}>
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: 400, md: 500 },
        overflow: 'hidden',
      }}
    >
      {/* Google Map Embed with Abuja address */}
      <iframe
        title="Grand Luxe Hotel Location"
        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3940.715015367603!2d7.462229074995789!3d9.05725329106861!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x104e0b3b6b9d36d9%3A0x6f3b4b4b4b4b4b4b!2s1%20Bria%20Street%2C%20Wuse%202%2C%20Abuja%2C%20Nigeria!5e0!3m2!1sen!2sng!4v1688755555555!5m2!1sen!2sng"
        width="100%"
        height="100%"
        style={{ border: 0 }}
        allowFullScreen=""
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      ></iframe>

      {/* Overlay Info Box */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 'auto', md: 40 },
          bottom: { xs: 20, md: 'auto' },
          left: { xs: '50%', md: 40 },
          transform: { xs: 'translateX(-50%)', md: 'none' },
          bgcolor: 'background.paper',
          boxShadow: 6,
          borderRadius: 3,
          p: 3,
          maxWidth: 320,
          textAlign: { xs: 'center', md: 'left' },
        }}
      >
        <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
          Grand Luxe Hotel
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          1 Bria Street, Wuse 2<br />
          Abuja, Nigeria
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          <strong>Phone:</strong> +234 8065514429<br />
          <strong>Email:</strong> info@grandluxe.com
        </Typography>
        <Button
          variant="contained"
          size="medium"
          sx={{ borderRadius: 2 }}
          href="https://www.google.com/maps/dir/?api=1&destination=1+Bria+Street+Wuse+2+Abuja"
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<LocationOn />}
        >
          Get Directions
        </Button>
      </Box>
    </Box>
  </Container>
</Box>

      {/* Footer */}
      <Footer />

      {/* WhatsApp Float Button */}
      <Fab
        color="primary"
        aria-label="whatsapp"
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
        onClick={() => window.open('https://wa.me/2348065514429', '_blank')}
      >
        <WhatsApp />
      </Fab>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <style jsx global>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};

export default ContactPage;